import { SupplierAutocomplete } from '@/components/company/SupplierAutocomplete';
import { CustomersAutocomplete } from '@/components/customer/CustomersAutocomplete';
import { entityReferencePicker } from './$taskId/view/entityReferenceEdit';
import type { AnchorReferenceColumn } from './columns/anchorReferenceColumns';
import type { TasksQueueSearch } from './taskQueueSearch';

/**
 * One picker per visible anchor reference column whose record type has a
 * search control (supplier, customer). A pick filters the queue to tasks
 * whose anchor points at that record; it is kept in the URL's `ref` param.
 */
export function AnchorReferenceFilters({
  columns,
  selections,
  updateSearchParams,
}: {
  columns: readonly AnchorReferenceColumn[] | undefined;
  /** The URL's `ref` param: anchor path → selected record id. */
  selections: TasksQueueSearch['ref'];
  updateSearchParams: (
    updates: Partial<TasksQueueSearch>,
    resetPage?: boolean,
  ) => void;
}) {
  function select(path: string, id: string | undefined) {
    const { [path]: _previous, ...rest } = selections ?? {};
    const next = id ? { ...rest, [path]: id } : rest;
    updateSearchParams(
      { ref: Object.keys(next).length > 0 ? next : undefined },
      true,
    );
  }

  return (columns ?? []).map((column) => {
    const selected = selections?.[column.path];
    switch (entityReferencePicker(column.entityType)) {
      case 'supplier':
        return (
          <div
            key={column.columnId}
            className="w-56"
            title={column.header}
            data-testid={`task-queue-ref-filter-${column.path}`}
          >
            <SupplierAutocomplete
              supplierId={selected}
              onSelect={(id) => select(column.path, id)}
            />
          </div>
        );
      case 'customer':
        return (
          <div
            key={column.columnId}
            className="w-56"
            title={column.header}
            data-testid={`task-queue-ref-filter-${column.path}`}
          >
            <CustomersAutocomplete
              selectedCustomerId={selected}
              onCustomerSelect={(id) => select(column.path, id)}
              allowClear
            />
          </div>
        );
      case 'record':
        return null;
    }
  });
}
